import React from 'react';
import clsx from 'clsx';
import { Spinner } from './Spinner';
import { Card } from './Card';
// --- Type Definitions for Table Props ---

/**
 * This is the TYPE DEFINITION for a single column. It is not a component.
 * It tells the Table component what to render in the header and how to get/render the data for each cell.
 * @template T The type of the data object for each row (e.g., AgentVersion).
 */
export interface TableColumn<T> {
  /** A unique key for the column, usually a property from your data object. */
  key: keyof T | 'actions';
  /** The text to display in the table header. */
  header: string;
  headerClassName?: string;
  cellClassName?: string;
  /** An optional function to render custom content for a cell. If not provided, it will display the direct value. */
  render?: (item: T, index: number) => React.ReactNode;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  title?: string;
  emptyStateMessage?: string;
  rowClassName?: string;
  onRowClick?: (item: T) => void;
  isLoading?: boolean;
}

// --- The Table Component ---

// The `id` constraint ensures we have a stable key for rows.
export const Table = <T extends { id: string | number }>({
  columns,
  data,
  title,
  emptyStateMessage = 'No data available.',
  rowClassName,
  onRowClick,
  isLoading = false,
}: TableProps<T>) => {
  return (
    <Card title={title} noPadding>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className={clsx(
            // Neobrutalism
            'border-b-2 border-black bg-slate-50',
            // Liquid glass
            '[.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:bg-white/5'
          )}>
            <tr>
              {columns.map((col) => (
                <th
                  key={String(col.key)}
                  scope="col"
                  className={clsx(
                    'p-4 text-sm font-bold',
                    // Neobrutalism
                    'text-slate-900',
                    // Liquid glass
                    '[.theme-liquid-glass_&]:text-white',
                    col.headerClassName
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="p-8 text-center text-slate-500">
                  <Spinner text="Loading..." />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="p-8 text-center text-slate-500">
                  {emptyStateMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr
                  key={item.id}
                  className={clsx(
                    'last:border-b-0',
                    // Neobrutalism
                    'border-b border-slate-200',
                    // Liquid glass
                    '[.theme-liquid-glass_&]:border-white/10',
                    rowClassName,
                    onRowClick && 'cursor-pointer transition-colors hover:bg-slate-50 [.theme-liquid-glass_&]:hover:bg-white/5'
                  )}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((col) => (
                    <td
                      key={`${String(col.key)}-${item.id}`}
                      className={clsx(
                        'p-4 text-sm align-top',
                        // Neobrutalism
                        'text-slate-700',
                        // Liquid glass
                        '[.theme-liquid-glass_&]:text-white/80',
                        col.cellClassName
                      )}
                    >
                      {col.render
                        ? col.render(item, index)
                        // This line correctly accesses the property if no render function is provided.
                        : (item[col.key as keyof T] as React.ReactNode)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};